/**
 * useTypingIndicator — owns BOTH sides of the typing protocol:
 *   - sender: debounced `typing.start` / auto-`typing.stop` on idle
 *   - receiver: state of the OTHER party's typing, auto-hide on stale
 *
 * Architectural notes:
 *   - The hook is the single source of truth for typing state. The
 *     Chatroom component is purely presentational over its return.
 *   - All timers + the "have we sent start yet?" flag live in refs so
 *     React strict-mode double-mounts don't double-fire frames.
 *   - The hook does NOT own the WebSocket — it takes a `send` callback
 *     so it remains decoupled from the connection lifecycle (the
 *     parent owns reconnect / give-up handling).
 *
 * Frame contract:
 *   sent     { t: 'typing', state: 'start' | 'stop' }
 *   received { t: 'typing', state: 'start' | 'stop', role, name }
 *
 * Defensive timing:
 *   IDLE_STOP_MS    (4s) — sender stops emitting after this long
 *                          without keystrokes
 *   AUTO_HIDE_MS    (6s) — receiver clears its indicator if no stop
 *                          frame arrives within this long after a
 *                          start. Belt-and-suspenders against a
 *                          sender that crashes mid-burst.
 */
import { useCallback, useEffect, useRef, useState } from 'react'

const IDLE_STOP_MS = 4_000
const AUTO_HIDE_MS = 6_000

export interface RemoteTypingState {
  /** True iff the OTHER party is typing right now. */
  readonly isTyping: boolean
  /** Role of the typer — useful for "Customer" vs "Agent" labelling. */
  readonly role: 'agent' | 'customer' | null
  /** Display name from the typing frame, if any. */
  readonly name: string | null
}

export interface TypingIndicator {
  readonly remote: RemoteTypingState
  /**
   * Call from the input's onChange handler. Manages debounced
   * start/stop emission internally. Idempotent — safe to call on
   * every keystroke.
   */
  onLocalInputChange(value: string): void
  /**
   * Call when the local user just sent a message OR otherwise
   * "intentionally stopped" (blur, leave). Forces an immediate stop
   * frame and resets the debounce flag.
   */
  forceStop(): void
  /**
   * Call from the WS frame handler when a `typing` frame arrives.
   */
  onRemoteFrame(state: 'start' | 'stop', role: 'agent' | 'customer', name: string | null): void
  /**
   * Call when ANY non-typing frame from the remote arrives — typically
   * a `msg`. Defensive: if the remote sent a message, they're
   * obviously not still typing. Clears local state without waiting
   * for an explicit stop frame (which a buggy client might forget).
   */
  onRemoteActivity(): void
}

export function useTypingIndicator(
  send: (frame: { t: 'typing'; state: 'start' | 'stop' }) => void,
): TypingIndicator {
  const [remote, setRemote] = useState<RemoteTypingState>({
    isTyping: false,
    role: null,
    name: null,
  })

  // Sender-side state — refs so strict-mode double-mount doesn't reset
  // the "we've already sent start" flag mid-burst.
  const startedRef = useRef(false)
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Receiver-side state — auto-hide timer if the remote disconnects
  // mid-typing without sending a stop frame.
  const autoHideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const clearIdleTimer = (): void => {
    if (idleTimerRef.current !== null) {
      clearTimeout(idleTimerRef.current)
      idleTimerRef.current = null
    }
  }
  const clearAutoHideTimer = (): void => {
    if (autoHideTimerRef.current !== null) {
      clearTimeout(autoHideTimerRef.current)
      autoHideTimerRef.current = null
    }
  }

  const sendStop = useCallback((): void => {
    if (!startedRef.current) return
    startedRef.current = false
    clearIdleTimer()
    try {
      send({ t: 'typing', state: 'stop' })
    } catch {
      // WS may have closed mid-flight — local state is already
      // reset, so nothing further to do.
    }
  }, [send])

  const onLocalInputChange = useCallback(
    (value: string): void => {
      const isComposing = value.length > 0
      if (!isComposing) {
        // User cleared the input — explicit stop.
        sendStop()
        return
      }
      // First keystroke of a burst → emit start exactly once.
      if (!startedRef.current) {
        startedRef.current = true
        try {
          send({ t: 'typing', state: 'start' })
        } catch {
          // If the send fails, leave startedRef true — the WS may be
          // reconnecting. We'll send stop on next clear/blur.
        }
      }
      // Reset the idle timer on every keystroke. After IDLE_STOP_MS
      // of no further keystrokes, emit stop automatically (matches
      // the WhatsApp / Slack pattern — the user paused typing).
      clearIdleTimer()
      idleTimerRef.current = setTimeout(sendStop, IDLE_STOP_MS)
    },
    [send, sendStop],
  )

  const forceStop = useCallback((): void => {
    sendStop()
  }, [sendStop])

  const onRemoteFrame = useCallback(
    (state: 'start' | 'stop', role: 'agent' | 'customer', name: string | null): void => {
      if (state === 'start') {
        setRemote({ isTyping: true, role, name })
        // Arm / re-arm the auto-hide guard — if no stop arrives in
        // AUTO_HIDE_MS, clear the indicator. Belt-and-suspenders
        // against a sender that crashes mid-burst without ever
        // sending the trailing stop.
        clearAutoHideTimer()
        autoHideTimerRef.current = setTimeout(() => {
          setRemote({ isTyping: false, role: null, name: null })
        }, AUTO_HIDE_MS)
      } else {
        clearAutoHideTimer()
        setRemote({ isTyping: false, role: null, name: null })
      }
    },
    [],
  )

  const onRemoteActivity = useCallback((): void => {
    // Any non-typing frame from the other side implies they're not
    // typing right now (e.g. they just sent a message).
    clearAutoHideTimer()
    setRemote({ isTyping: false, role: null, name: null })
  }, [])

  // Cleanup on unmount: cancel timers + best-effort stop frame so the
  // remote's UI doesn't show "is typing" forever.
  useEffect(() => {
    return () => {
      sendStop()
      clearAutoHideTimer()
    }
  }, [sendStop])

  return {
    remote,
    onLocalInputChange,
    forceStop,
    onRemoteFrame,
    onRemoteActivity,
  }
}
